<script lang="ts">
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { get_local_ip, real_id } from '../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	//Services
	import { localReq, postReq } from '../../../services/http';
	//Stores
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { Toggle, ToggleV2, Checkbox, Select, SelectV2, Button, Alert } from '@Shelly/shelly-design-system';
	//Types
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { Device } from '../../../types/Device';
	import { capFirstChar } from '../../../helpers/utilities';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import SettingGuide from '../SettingGuide.svelte';

	export let device: Device;
	export let isOffline;

	const id = real_id(device.id),
		props = ['-50', '-55', '-60', '-65', '-70', '-75', '-80', '-85', '-90'],
		settings = readDeviceSettings(id),
		handleRoaming = async () => {
			waitForResponse = true;

			const local_ip = get_local_ip(id);

			const res: SettingRequest = local_ip
					? await localReq(device, `${local_ip}/settings`, 'post', {
							ap_roaming_enabled: enabled,
							ap_roaming_threshold: Number(threshold),
						})
					: await postReq(`/device/generic_command`, {
							id,
							command: 'ap_roaming',
							payload: JSON.stringify({
								enabled: Number(enabled),
								threshold: Number(threshold),
							}),
						}),
				toastTexts = {
					success: enabled
						? $format('ap_roaming_scc_toast', {
								default: 'AP roaming value saved.',
							})
						: $format('ap_roaming_disable_scc_toast', {
								default: 'AP roaming disabled successfully!',
							}),
					danger: $format('ap_roaming_err_toast', {
						default: 'Failed to set AP roaming value.',
					}),
				};
			onSettingsResponse(res, device, toastTexts);
			waitForResponse = false;
		};

	let enabled = settings?.ap_roaming.enabled,
		threshold = String(settings?.ap_roaming.threshold),
		waitForResponse = false;

	const onToggleChange = () => {
		enabled = !enabled;
		handleRoaming();
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && enabled) {
			stickyBottomAction.set({
				onClick: handleRoaming,
				loading: waitForResponse,
				disabled: isOffline,
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});

	$: SelectC = $showNewLayout ? SelectV2 : Select;
</script>

<SettingGuide
	text={$format('settings_guide_wifi_ap_roaming', {
		default:
			'Switch to a stronger access point when the signal drops below a threshold. Useful on mesh networks.',
	})}
/>

{#if $showNewLayout}
	<ToggleV2
		size="m"
		class="mb-s-plus"
		checked={enabled}
		on:click={onToggleChange}
		disabled={isOffline}
		id="checkbox-q73s17a"
	>
		{capFirstChar($format('option_enable'))}
	</ToggleV2>
	{#if enabled}
		<Alert
			class="mb-s"
			heading={$format('ap_roaming_description', {
				default:
					'RSSI signal strength value below which the device will periodically scan for better access point:',
			})}
		/>
		<svelte:component
			this={SelectC}
			aria-label={$format('select_ap_roaming_threshold', {
				default: 'Select AP roaming threshold',
			})}
			label={$format('select_ap_roaming_threshold', {
				default: 'Select AP roaming threshold',
			})}
			size="m"
			disabled={!enabled || isOffline}
			bind:value={threshold}
			options={props}
			icon="fas fa-signal"
			id="select-u1l24yl"
		/>
	{/if}
{:else}
	<Checkbox size="l" class="mb-s" bind:checked={enabled} disabled={isOffline} id="checkbox-q73s17a">
		{capFirstChar($format('option_enable'))}
	</Checkbox>

	<hr class="mb-s" />

	<Alert
		class="mb-s"
		heading={$format('ap_roaming_description', {
			default:
				'RSSI signal strength value below which the device will periodically scan for better access point:',
		})}
	/>
	<svelte:component
		this={SelectC}
		aria-label={$format('select_ap_roaming_threshold', {
			default: 'Select AP roaming threshold',
		})}
		label={$format('select_ap_roaming_threshold', {
			default: 'Select AP roaming threshold',
		})}
		size="m"
		class="mb-s"
		disabled={!enabled || isOffline}
		bind:value={threshold}
		options={props}
		icon="fas fa-signal"
		id="select-u1l24yl"
	/>
	<Button
		size="m"
		width="full"
		on:click={handleRoaming}
		loading={waitForResponse}
		disabled={isOffline}
	>
		{$format('button_save', { default: 'save' })}
	</Button>
{/if}
